import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import StructuredData, { breadcrumbSchema } from "../components/StructuredData";

const SITE = "https://makeupbrushbasics.com";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      });
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <div className="overline mb-3">404</div>
        <h1 className="font-serif-display text-4xl text-stone-900">Post not found.</h1>
        <p className="mt-4 text-stone-600">The essay you were looking for isn&apos;t here.</p>
        <Link to="/blog" className="mt-8 inline-block text-xs uppercase tracking-[0.22em] text-[#8C6A5D] hover:text-[#70544A]">← Back to The Journal</Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <p className="text-sm text-stone-500">Loading…</p>
      </div>
    );
  }

  const url = `${SITE}/blog/${post.slug}`;
  const img = post.cover_image?.startsWith("http") ? post.cover_image : `${SITE}${post.cover_image}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: img,
    datePublished: post.published,
    dateModified: post.published,
    author: { "@type": "Organization", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "BrushBasics",
      logo: { "@type": "ImageObject", url: `${SITE}/images/og-default.jpg` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  return (
    <article data-testid="blog-post" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={img}
      />
      <StructuredData
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "The Journal", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          articleSchema,
        ]}
      />

      <Link to="/blog" className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-[#8C6A5D] transition-colors">
        ← The Journal
      </Link>

      <header className="mt-8 mb-10">
        <div className="overline mb-3">
          {new Date(post.published).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
          {" · "}
          {post.reading_min} min read
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-stone-900 tracking-tight">
          {post.title}
        </h1>
      </header>

      {post.cover_image && (
        <div className="mb-12 aspect-[16/9] overflow-hidden bg-[#F2EFEB] border border-[#E5E0D8]">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="bb-prose">
        <ReactMarkdown
          components={{
            h2: ({ node, ...props }) => <h2 className="font-serif-display text-2xl sm:text-3xl text-stone-900 font-light mt-14 mb-4" {...props} />,
            p: ({ node, ...props }) => <p className="text-base sm:text-lg leading-[1.85] text-stone-700 mb-6" {...props} />,
            em: ({ node, ...props }) => <em className="italic text-stone-600" {...props} />,
            strong: ({ node, ...props }) => <strong className="text-stone-900 font-medium" {...props} />,
            ul: ({ node, ...props }) => <ul className="list-disc list-outside ml-6 mb-6 space-y-2 text-stone-700 text-base sm:text-lg leading-relaxed" {...props} />,
            li: ({ node, ...props }) => <li className="pl-2" {...props} />,
            a: ({ node, ...props }) => <a className="text-[#8C6A5D] underline underline-offset-4 hover:text-[#70544A] transition-colors" {...props} />,
            hr: () => <hr className="border-[#E5E0D8] my-14" />,
          }}
        >
          {post.body}
        </ReactMarkdown>
      </div>

      <div className="mt-16 pt-10 border-t border-[#E5E0D8]">
        <div className="overline mb-3">Keep reading</div>
        <div className="flex flex-wrap gap-4">
          <Link to="/blog" className="text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB] px-6 py-3 transition-colors">
            More essays
          </Link>
          <Link to="/catalog" className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] text-white hover:bg-[#70544A] px-6 py-3 transition-colors">
            Browse the catalog
          </Link>
        </div>
      </div>
    </article>
  );
}
